import React, { useEffect, useState } from 'react'
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup,
  FormControl,
  DropdownButton,
  FloatingLabel,
  Dropdown,
  Table,
  Alert,
  FormSelect,
  Modal
} from 'react-bootstrap'


import { useForm } from 'react-hook-form'
import { CONFIG } from '../../config/constant'
import { BASE } from '../../basedata/constants'
import DatePicker, { registerLocale } from 'react-datepicker'
import ptBR from 'date-fns/locale/pt-BR'

registerLocale('pt-br', ptBR)

import 'react-datepicker/dist/react-datepicker.css'

import clubLogo from '../../images/club_logo.png'
import bentoLogo from '../../assets/images/logo_sao_bento.png'
import userTest from '../../assets/images/user/jeff_test.jpeg'
import netTest from '../../assets/images/user/neg.jpeg'
import Loader from '../../shared/loader'
import {
  hasAuthenticatedSession,
  getCompanyBrandName,
  getCompanyLogoUrl,
  getCurrentCompanyProfile
} from '../../config/session'
import { fetchWithApiKey } from '../../config/apiCall'
import {
  redirectToLoginWithSessionNotice,
  SessionExpiredError
} from '../../config/authFlow'
import UserBiometryImage from './UserBiometryImage'
import {
  getGateCode,
  getTicketGateCode,
  isCorrectGate
} from '../../config/gates'
import { useGateOptions } from '../../hooks/useGateOptions'
import GateSelector from './GateSelector'

const resolveTicketImageSrc = (image: any) => {
  const normalizedImage = `${image || ''}`.trim()

  if (!normalizedImage || normalizedImage === '---') {
    return ''
  }

  if (normalizedImage.startsWith('data:image')) {
    return normalizedImage
  }

  return normalizedImage
}

const getPrimaryTicket = (ticketData: any) => {
  if (Array.isArray(ticketData)) {
    return ticketData[0] || null
  }

  if (ticketData && typeof ticketData === 'object') {
    return ticketData
  }

  return null
}

const getTicketIdentifier = (ticketData: any, fallback = '') => {
  const primaryTicket = getPrimaryTicket(ticketData)
  const fallbackTicket =
    typeof fallback === 'string' || typeof fallback === 'number'
      ? `${fallback}`.trim()
      : ''

  return (
    primaryTicket?.hash ||
    primaryTicket?.token ||
    primaryTicket?.tokens?.[0]?.token ||
    primaryTicket?.external_id ||
    primaryTicket?.externalId ||
    fallbackTicket
  )
}

const pickFirstFilledValue = (...values: any[]) => {
  for (const value of values) {
    if (value === undefined || value === null) {
      continue
    }

    const normalizedValue = `${value}`.trim()

    if (normalizedValue) {
      return normalizedValue
    }
  }

  return ''
}

const isUuidLike = (value: any) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    `${value || ''}`.trim()
  )

const formatTicketDateTime = (value: any) => {
  const normalizedValue = `${value || ''}`.trim()

  if (!normalizedValue) {
    return {
      date: '',
      time: '',
      dateTime: ''
    }
  }

  const parsedDate = new Date(normalizedValue)

  if (Number.isNaN(parsedDate.getTime())) {
    return {
      date: '',
      time: '',
      dateTime: normalizedValue
    }
  }

  const date = parsedDate.toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo'
  })
  const time = parsedDate.toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit'
  })

  return {
    date,
    time,
    dateTime: `${date} ${time}`.trim()
  }
}

const normalizeTicketResponse = (response: any) => {
  const primaryTicket = getPrimaryTicket(response.ticket)
  const ticketIdentifier = getTicketIdentifier(response.ticket, response.ticket)
  const sessionDate = response.sessionDate || primaryTicket?.sessionDate || ''
  const sessionTime = response.sessionTime || primaryTicket?.sessionTime || ''
  const validFrom = response.validFrom || primaryTicket?.validFrom || ''
  const formattedValidFrom = formatTicketDateTime(validFrom)
  const gameDate = response.gameDate || sessionDate || formattedValidFrom.date || ''
  const gameHour = response.gameHour || sessionTime || formattedValidFrom.time || ''
  const gameTime =
    response.gameTime ||
    [gameDate, gameHour].filter(Boolean).join(' ') ||
    formattedValidFrom.dateTime
  const responseGame = `${response.game || ''}`.trim()
  const externalEventId = `${response.gameCode || primaryTicket?.external_event_id || ''}`.trim()
  const game =
    response.gameLabel ||
    response.gameName ||
    response.matchName ||
    response.eventName ||
    (!isUuidLike(responseGame) ? responseGame : '') ||
    externalEventId ||
    responseGame
  const sectorId = pickFirstFilledValue(
    response.sectorId,
    response.sector,
    response.section,
    response.secao,
    primaryTicket?.sectorId,
    primaryTicket?.sector,
    primaryTicket?.section,
    primaryTicket?.secao
  )
  const row = pickFirstFilledValue(
    response.row,
    response.rowNumber,
    response.rowLabel,
    response.rowName,
    response.line,
    response.lineNumber,
    response.fila,
    primaryTicket?.row,
    primaryTicket?.rowNumber,
    primaryTicket?.rowLabel,
    primaryTicket?.rowName,
    primaryTicket?.line,
    primaryTicket?.lineNumber,
    primaryTicket?.fila,
    primaryTicket?.seatRow,
    primaryTicket?.seat_row
  )
  const seat = pickFirstFilledValue(
    response.seat,
    response.seatNumber,
    response.seatLabel,
    response.seatName,
    response.chair,
    response.chairNumber,
    response.place,
    response.placeNumber,
    response.assento,
    response.sit,
    primaryTicket?.seat,
    primaryTicket?.seatNumber,
    primaryTicket?.seatLabel,
    primaryTicket?.seatName,
    primaryTicket?.chair,
    primaryTicket?.chairNumber,
    primaryTicket?.place,
    primaryTicket?.placeNumber,
    primaryTicket?.assento,
    primaryTicket?.sit
  )
  const block = pickFirstFilledValue(
    response.block,
    response.blockName,
    response.bloco,
    primaryTicket?.block,
    primaryTicket?.blockName,
    primaryTicket?.bloco
  )
  const gateCode = pickFirstFilledValue(
    response.gateId,
    response.gateCode,
    response.integration_code,
    response.integrationCode,
    response.ingPortao,
    primaryTicket?.gateId,
    primaryTicket?.gateCode,
    primaryTicket?.integration_code,
    primaryTicket?.integrationCode,
    primaryTicket?.ingPortao,
    response.gate,
    primaryTicket?.gate
  )
  const gate = pickFirstFilledValue(
    response.gateName,
    response.gateAlias,
    response.alias,
    primaryTicket?.gateName,
    primaryTicket?.gateAlias,
    primaryTicket?.alias,
    response.gate,
    primaryTicket?.gate,
    gateCode
  )

  return {
    ...response,
    image: resolveTicketImageSrc(response.image),
    name: response.name || primaryTicket?.holderName || '',
    ticket: ticketIdentifier,
    ticketRaw: Array.isArray(response.ticketHistory) && response.ticketHistory.length > 0
      ? response.ticketHistory
      : response.ticket,
    gameId: responseGame,
    externalEventId,
    game,
    gameDate,
    gameHour,
    gameTime,
    sectorId,
    sector: sectorId,
    gateId: gateCode,
    gateCode,
    gate,
    ingPortao: gateCode || '',
    block,
    row,
    seat,
    sit: seat,
    ticketStatus:
      response.ticketStatus ||
      (primaryTicket?.checkInLogs ? 'invalid' : primaryTicket?.status || ''),
    ticketUsed:
      response.ticketUsed && response.ticketUsed.date
        ? response.ticketUsed
        : primaryTicket?.checkInLogs
          ? {
              date: gameDate,
              time: gameHour,
              gate: gate || ''
            }
          : response.ticketUsed
  }
}

const getEventNameCacheKey = (identifier: string) => `event_name_${identifier}`

const getEventLookupBaseUrls = () => {
  const candidates = [BASE.ticket_api.base_url, 'http://127.0.0.1:3105']
  return Array.from(
    new Set(
      candidates
        .map((value) => `${value || ''}`.trim().replace(/\/$/, ''))
        .filter(Boolean)
    )
  )
}

const fetchEventName = async (identifier: string, isExternalId = false) => {
  const normalizedIdentifier = `${identifier || ''}`.trim()

  if (!normalizedIdentifier) {
    return ''
  }

  const cacheKey = getEventNameCacheKey(normalizedIdentifier)
  const cachedName = sessionStorage.getItem(cacheKey)
  if (cachedName) {
    return cachedName
  }

  const path = isExternalId
    ? `/event/external/id/${normalizedIdentifier}`
    : `/event/${normalizedIdentifier}`

  for (const baseUrl of getEventLookupBaseUrls()) {
    try {
      const response = await fetchWithApiKey(`${baseUrl}${path}`, {
        method: 'GET'
      })

      if (!response.ok) {
        continue
      }

      const payload = await response.json()
      const eventName = `${payload?.event?.eventName || payload?.event?.title || payload?.event?.name || payload?.name || payload?.title || ''}`.trim()

      if (eventName) {
        sessionStorage.setItem(cacheKey, eventName)
        return eventName
      }
    } catch (error) {
      console.error('Erro ao buscar nome do evento', error)
    }
  }

  return ''
}

const enrichTicketResponseWithEventName = async (response: any) => {
  const normalizedResponse = normalizeTicketResponse(response)
  const gameId = `${normalizedResponse.gameId || ''}`.trim()
  const externalEventId = `${normalizedResponse.externalEventId || ''}`.trim()
  const currentGame = `${normalizedResponse.game || ''}`.trim()

  if (currentGame && !isUuidLike(gameId) && currentGame !== externalEventId) {
    return normalizedResponse
  }

  let eventName = ''

  if (isUuidLike(gameId)) {
    eventName = await fetchEventName(gameId)
  }

  if (!eventName && externalEventId) {
    eventName = await fetchEventName(externalEventId, true)
  }

  if (!eventName) {
    return normalizedResponse
  }

  return {
    ...normalizedResponse,
    game: eventName
  }
}

const maskCPF = (value: any) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1')
}

// (00) 00000-0000
const maskPhone = (value: any) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d{4})(\d)/, '$1-$2')
}

// 00000-000
const maskCEP = (value: any) => {
  return value.replace(/\D/g, '').replace(/^(\d{5})(\d{3})+?$/, '$1-$2')
}

// 00/00/0000
const maskDate = (value: any) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d)/, '$1')
}

const isValidEmail = (email: any) =>
  // eslint-disable-next-line no-useless-escape
  /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email
  )

const strongPassword = (password: any) => {
  return /(?=^.{8,}$)(?=.*\d)(?=.*[!@#$%^&*]+)(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/.test(
    password
  )
}

interface Props {
  onResponse: (response: any) => void
}

type ContingencyReason = {
  id: number
  name: string
  isActive: boolean
  shouldDeleteBiometry: boolean
}

const SearchTicket = ({ onResponse }: Props) => {
  const {
    gateOptions,
    selectedGate,
    selectGateByCode,
    isLoading: isGateSelectionLoading,
    error: gateOptionsError
  } = useGateOptions()
  const selectedGateCode = getGateCode(selectedGate)
  const resolvedClubLogo = getCompanyLogoUrl() || clubLogo
  const clubBrandName = getCompanyBrandName()
  const companyProfile = getCurrentCompanyProfile()
  const enforceGateValidation = Boolean(companyProfile.enforceGateValidation)
  const wrongGateClass = companyProfile.slug === 'gremio' ? 'blue' : 'red'

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    clearErrors,
    getValues,
    reset
  } = useForm()

  const [startDate, setStartDate] = useState(new Date())

  const [hasToken, setHasToken] = useState('')

  const [disableSign, setDisableSign] = useState(false)

  const [userNotFound, setUserNotFound] = useState(false)

  const [motivation, setMotivation] = useState({
    show: false,
    case: 0
  })
  const [contingencyReasons, setContingencyReasons] = useState<
    ContingencyReason[]
  >([])
  const [isLoadingContingencyReasons, setIsLoadingContingencyReasons] =
    useState(false)
  const [contingencyReasonsError, setContingencyReasonsError] = useState('')
  const [purpleCodeWarningVisible, setPurpleCodeWarningVisible] = useState(false)
  const [gateSelectionWarningVisible, setGateSelectionWarningVisible] =
    useState(false)

  const [loading, setLoading] = useState(false)

  const [userData, setUserData] = useState({
    image: '---',
    name: '',
    documentType: '',
    document: '',
    birthday: '',
    transaction: '',
    game: '',
    gameDate: '',
    gameHour: '',
    gameTime: '',
    sector: '',
    sectorId: '',
    gateId: '',
    gateCode: '',
    gate: '',
    block: '',
    row: '',
    seat: '',
    sit: '',
    ticket: '',
    ticketStatus: '',
    ticketUsed: {
      date: '',
      time: '',
      gate: ''
    },
    ingPortao: '',
    supervisor: false,
    ticketRaw: [] as any[]
  })

  const [searchUser, setSearchUser] = useState('')

  const hasSupervisorBlock = [userData.seat, userData.sit]
    .some((value) => typeof value === 'string' && value.toLowerCase() === 'blocked') || userData.supervisor === true

  const rawTickets = Array.isArray(userData.ticketRaw) ? userData.ticketRaw : []
  const hasMultipleTickets = rawTickets.length > 1
  const primaryTicket = getPrimaryTicket(userData.ticketRaw)
  const primaryCheckInLogs = primaryTicket?.checkInLogs && typeof primaryTicket.checkInLogs === 'object'
    ? primaryTicket.checkInLogs as { hash?: string; createdAt?: string; sector?: string }
    : null
  const ticketGateCode = getTicketGateCode(userData)
  const isSelectedGateCorrect = true
  const shouldBlockSearchByGate =
    enforceGateValidation && (!selectedGateCode || isGateSelectionLoading)

  const [newUser, setNewUser] = useState({
    login: '',
    password: '',
    set: false
  })

  const handleGateChange = (gateCode: string) => {
    selectGateByCode(gateCode)
    setSearchUser('')
    setUserNotFound(false)
    setPurpleCodeWarningVisible(false)
    setGateSelectionWarningVisible(false)
    sessionStorage.removeItem('ticket')
    sessionStorage.removeItem('usedTicket')
    sessionStorage.removeItem('contingencyReasonId')
  }

  useEffect(() => {
    if (selectedGateCode) {
      setGateSelectionWarningVisible(false)
    }
  }, [selectedGateCode])

  useEffect(() => {
    if (!motivation.show) {
      return
    }

    let isActive = true

    const loadContingencyReasons = async () => {
      setIsLoadingContingencyReasons(true)
      setContingencyReasonsError('')

      try {
        const response = await fetchWithApiKey(
          `${BASE.ticket_api.base_url}/tickets/contingency-reasons`,
          {
            method: 'GET'
          }
        )

        if (!response.ok) {
          throw new Error('Falha ao carregar motivos')
        }

        const payload = await response.json()
        const reasons = Array.isArray(payload) ? payload : []

        if (isActive) {
          setContingencyReasons(reasons)
        }
      } catch (error) {
        console.error('Erro ao carregar motivos de contingencia:', error)

        if (isActive) {
          setContingencyReasons([])
          setContingencyReasonsError(
            'Nao foi possivel carregar os motivos de contingencia.'
          )
        }
      } finally {
        if (isActive) {
          setIsLoadingContingencyReasons(false)
        }
      }
    }

    loadContingencyReasons()

    return () => {
      isActive = false
    }
  }, [motivation.show])

  const onSubmit = (data: any) => {
    if (!hasAuthenticatedSession()) {
      redirectToLoginWithSessionNotice()
      return
    }

    if (shouldBlockSearchByGate) {
      setGateSelectionWarningVisible(true)
      return
    }

    sessionStorage.removeItem('ticket')
    sessionStorage.removeItem('usedTicket')
    sessionStorage.removeItem('contingencyReasonId')

    setUserNotFound(false)
    setSearchUser('')
    setPurpleCodeWarningVisible(false)
    setGateSelectionWarningVisible(false)
    setLoading(true)

    // if(data.document === '888'){
    //   setUserNotFound(true)
    // }else{
    //   setSearchUser(data.document)
    // }
    // setDisableSign(true)

    fetchWithApiKey(`${BASE.ticket_api.base_url}${BASE.ticket_api.user_validate}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        value: data.document.replace(/\./g, '').replace(/-/g, '').replace(/^\s+|\s+$/g, ''),
        search: data.type,
      })
    })
      .then(async (res) => {
        if (res.status === 401 || res.status === 403) {
          redirectToLoginWithSessionNotice()
          return null
        }

        return res.json()
      })
      .then(async (response) => {
        if (!response) {
          setLoading(false)
          return
        }

        const normalizedResponse = await enrichTicketResponseWithEventName(response)

        setLoading(false)
        const hasTicket = Boolean(`${normalizedResponse.ticket || ''}`.trim())
        if (!normalizedResponse.document || !hasTicket) {
          setUserNotFound(true)
          return
        }
        const normalizedTicketGateCode = getTicketGateCode(normalizedResponse)
        const isCorrectSelectedGate =
          !enforceGateValidation ||
          isCorrectGate(normalizedTicketGateCode, selectedGateCode)
        setUserNotFound(false)
        setSearchUser(normalizedResponse.document)
        sessionStorage.setItem('ticket', normalizedResponse.ticket)
        sessionStorage.setItem('gameCode', (normalizedResponse.gameCode || '1111'))
        sessionStorage.setItem('gameId', normalizedResponse.gameId || '')
        sessionStorage.setItem('ingPortao', normalizedTicketGateCode || '-')
        sessionStorage.setItem('sectorId', normalizedResponse.sectorId || '-')
        sessionStorage.setItem('row', normalizedResponse.row || '-')
        sessionStorage.setItem('seat', normalizedResponse.seat || '-')
        sessionStorage.setItem('document', normalizedResponse.document || '-')
        sessionStorage.setItem('tokenBiometric', normalizedResponse.token || '-')
        if (
          normalizedResponse.ticketStatus === 'valid' ||
          normalizedResponse.ticketStatus === 'discounted'
        ) {
          sessionStorage.setItem('usedTicket', 'true')
        }
        setUserData(normalizedResponse)

        if (
          enforceGateValidation &&
          normalizedTicketGateCode &&
          selectedGateCode &&
          !isCorrectSelectedGate
        ) {
          sendWrongGate(normalizedResponse.ticket, selectedGateCode)
        }
      })
      .catch((error) => {
        setLoading(false)
        if (
          error instanceof SessionExpiredError ||
          error?.message === 'SESSION_EXPIRED' ||
          error?.name === 'SessionExpiredError'
        ) {
          redirectToLoginWithSessionNotice()
          return
        }
        setUserNotFound(true)
        console.log(error)
      })
  }

  const handlePurpleCodeBlockedAction = () => {
    setMotivation({
      show: false,
      case: 0
    })
    setPurpleCodeWarningVisible(true)
  }

  const handleMotivationFlow = () => {
    if (hasSupervisorBlock) {
      handlePurpleCodeBlockedAction()
      return
    }

    sessionStorage.removeItem('contingencyReasonId')
    setMotivation({
      show: true,
      case: 0
    })
  }

  const handleDirectAccessFlow = () => {
    if (hasSupervisorBlock) {
      handlePurpleCodeBlockedAction()
      return
    }

    sessionStorage.removeItem('contingencyReasonId')
    onResponse(1)
  }

  const motivationSubmit = (data: any) => {
    if (hasSupervisorBlock) {
      handlePurpleCodeBlockedAction()
      return
    }

    sessionStorage.setItem('contingencyReasonId', `${data.case}`)
    onResponse(1)
  }

  const sendWrongGate = (wrongTicket: any, wrongGate: any) => {
    fetchWithApiKey(`${BASE.ticket_api.base_url}${BASE.ticket_api.generateAccess}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        "ticket": wrongTicket,
        "gate": wrongGate,
        "deviceIp": "0.0.0.0",
        "registeredFace": "wrongGate",
        'status': 'waiting'
      })
    })
      .catch((error) => {
        console.log(error)
      })
  }
  const resetForm = () => {
    setNewUser({
      login: '',
      password: '',
      set: false
    })
    setHasToken('')
    setDisableSign(false)
    setContingencyReasons([])
    setContingencyReasonsError('')
    reset()
  }

  if (loading) {
    return (<Loader />)
  }

  const renderPurpleAwareActionButton = ({
    disabled,
    className,
    label,
    onClick,
    type = 'button'
  }: {
    disabled: boolean
    className: string
    label: string
    onClick: () => void
    type?: 'button' | 'submit'
  }) => {
    const shouldBlockForPurpleCode = hasSupervisorBlock
    const effectiveDisabled = disabled || shouldBlockForPurpleCode

    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <Button
          disabled={effectiveDisabled}
          type={type}
          className={className}
          onClick={!shouldBlockForPurpleCode ? onClick : undefined}
        >
          {label}
        </Button>
        {shouldBlockForPurpleCode && (
          <button
            type="button"
            aria-label="Código roxo identificado"
            onClick={handlePurpleCodeBlockedAction}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer'
            }}
          />
        )}
      </div>
    )
  }

  // 40418424845
  // setMotivation
  if (motivation.show) {
    return (
      <Form onSubmit={handleSubmit(motivationSubmit)}>
        <Table responsive striped >
          <tbody>
            <tr>
              <td>Foto</td>
            </tr>
            <tr>
              <td><UserBiometryImage imagePath={userData.image} imageSide={'face'} /></td>
            </tr>
            <tr>
              <td>Qual o motivo da contingência?</td>
            </tr>
            <tr>
              <td className='reason-radios'>
                {contingencyReasonsError && (
                  <Alert variant="danger" className="mb-3">
                    {contingencyReasonsError}
                  </Alert>
                )}

                {isLoadingContingencyReasons && (
                  <div className="text-muted">Carregando motivos...</div>
                )}

                {!isLoadingContingencyReasons &&
                  !contingencyReasonsError &&
                  contingencyReasons.map((item) => (
                    <Form.Check
                      key={item.id}
                      {...register('case', { required: true })}
                      label={item.name}
                      name="case"
                      type="radio"
                      value={`${item.id}`}
                      id={`${item.id}`}
                    />
                  ))}

                {!isLoadingContingencyReasons &&
                  !contingencyReasonsError &&
                  contingencyReasons.length === 0 && (
                    <Alert variant="warning" className="mb-0">
                      Nenhum motivo de contingência está ativo no momento.
                      Ative um motivo de contingência, por favor.
                    </Alert>
                  )}

                {errors.case && (
                  <span className="label-error">
                    Você precisa escolher uma causa
                  </span>
                )}

              </td>
            </tr>
            <tr>
              <td>
                <Button
                  disabled={
                    disableSign ||
                    isLoadingContingencyReasons ||
                    contingencyReasons.length === 0
                  }
                  type="submit"
                  className="mb-0 w-100"
                >
                  Prosseguir
                </Button>
              </td>
            </tr>
          </tbody>
        </Table>
      </Form>
    )
  }

  return (
    <>
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <Card.Title as="h5">
                <>
                  <span className='club-header'>
                    {clubBrandName}
                    <img src={resolvedClubLogo} alt={clubBrandName} />
                  </span>
                  <span className='club-header'>Controle de acesso</span>
                </>
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col>
                  <Form onSubmit={handleSubmit(onSubmit)}>
                    <GateSelector
                      options={gateOptions}
                      selectedGate={selectedGate}
                      isLoading={isGateSelectionLoading}
                      error={gateOptionsError}
                      onChange={handleGateChange}
                    />
                    {gateSelectionWarningVisible && (
                      <Alert variant="warning">
                        Selecione um portão para pesquisar.
                      </Alert>
                    )}
                    <Row className="mb-3">
                      <Col>
                        <Form.Label>Tipo do documento</Form.Label>
                        <FormSelect
                          className="form-control"
                          onChange={(event) => {
                            const { value } = event.target
                            // setReportType(value)
                          }}
                        >
                          <option value="access">CPF</option>
                          <option value="signup">Outro</option>
                        </FormSelect>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col>
                        <Form.Label>Número do documento</Form.Label>
                        <Form.Control placeholder="Número do documento" {...register('document')} />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Form.Group>
                          <Button
                            disabled={disableSign || shouldBlockSearchByGate}
                            type="submit"
                            className="mb-0 w-100"
                          >
                            Pesquisar
                          </Button>
                        </Form.Group>
                      </Col>
                    </Row>
                    {/* <Row>
                <p>Não foi possível cadastrar o usuário</p>
              </Row> */}
                  </Form>
                </Col>
              </Row>

              {newUser.set && (
                <>
                  <hr />
                  <Row>
                    <Col className="qr-wrapper">
                      <h3>Aprovador cadastrado com sucesso!</h3>
                    </Col>
                  </Row>
                </>
              )}
            </Card.Body>
          </Card></Col>
      </Row>
      {userNotFound && <Row>

        <Col>
          <Alert className='text-center' variant='danger'>Não encontrado</Alert>
        </Col>
      </Row>
      }
      {searchUser && <Row>
        <Col>
          <Card>
            <Card.Header>
              <Card.Title as="h5">
                <span className='club-header'>Dados do Ingresso</span>
              </Card.Title>
            </Card.Header>
            <Modal
              show={purpleCodeWarningVisible}
              onHide={() => setPurpleCodeWarningVisible(false)}
              centered
              backdrop="static"
              keyboard={true}
            >
              <Modal.Header closeButton>
                <Modal.Title>Código roxo identificado</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                Este ingresso exige atuação do supervisor responsável. A contingência não pode gerar QR Code nem liberar acesso neste fluxo.
              </Modal.Body>
              <Modal.Footer>
                <Button
                  type="button"
                  variant="primary"
                  className="w-100"
                  onClick={() => setPurpleCodeWarningVisible(false)}
                >
                  OK
                </Button>
              </Modal.Footer>
            </Modal>
            {hasSupervisorBlock &&
              <Button
                disabled={true}
                type="submit"
                className={`ticket-action purple`}
              >
                CONTACTAR SUPERVISOR
              </Button>
            }

            {!hasMultipleTickets && primaryCheckInLogs && (
              <div className="checkin-info">
                <div className="checkin-info__title">Check-in Registrado</div>
                <div className="checkin-info__grid">
                  <div>
                    <div className="checkin-info__label">Nº Ingresso</div>
                    <div className="checkin-info__value checkin-info__value--bold">#{primaryCheckInLogs.hash}</div>
                  </div>
                  <div>
                    <div className="checkin-info__label">Device</div>
                    <div className="checkin-info__value">{primaryCheckInLogs.sector || '-'}</div>
                  </div>
                  <div className="checkin-info__full-col">
                    <div className="checkin-info__label">Data e Hora</div>
                    <div className="checkin-info__value">
                      {primaryCheckInLogs.createdAt
                        ? formatTicketDateTime(primaryCheckInLogs.createdAt).dateTime
                        : '-'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!hasMultipleTickets && isSelectedGateCorrect &&
              renderPurpleAwareActionButton({
                disabled: disableSign,
                type: 'submit',
                className: `ticket-action ${(userData.ticketStatus === 'invalid') ? 'red' : (userData.ticketStatus === 'valid') ?  '': 'yellow'}`,
                label: (userData.ticketStatus === 'invalid') ? 'Acesso Já utilizado' : (userData.ticketStatus === 'valid') ? 'Gerar Acesso': 'Meia Entrada - Validar Ingresso',
                onClick: handleMotivationFlow
              })
            }

            {userData.ticketStatus === 'invalid' &&
              <Table responsive striped >
                <tbody>
                  <tr className='text-center'>
                    <td>Utilização</td>
                  </tr>
                  <tr>
                    <td className='text-center'>
                      {userData.ticketUsed.date.replace(/T/, ' ').replace(/Z/, ' ')} <br />
                      Portão {userData.gate}
                    </td>
                  </tr>
                </tbody></Table>}

            {hasMultipleTickets && (
              <Table responsive striped>
                <thead>
                  <tr>
                    <th>Titular</th>
                    <th>Ingresso</th>
                    <th>Sessão</th>
                    <th>Portão</th>
                    <th>Check-in</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rawTickets.map((t: any, index: number) => {
                    const token = t.hash || t.tokens?.[0]?.token || t.token || '-'
                    const ticketGate = getTicketGateCode(t)
                    const ticketGateDisplay = ticketGate || '-'
                    const isUsed = Boolean(t.checkInLogs)
                    const isTicketGateCorrect = true
                    const checkInLogs = t.checkInLogs && typeof t.checkInLogs === 'object' ? t.checkInLogs : null
                    const checkInTime = checkInLogs?.createdAt
                      ? formatTicketDateTime(checkInLogs.createdAt).dateTime
                      : '-'

                    return (
                      <tr key={`${token}-${index}`}>
                        <td>{t.holderName || userData.name || '-'}</td>
                        <td>{token}</td>
                        <td>{t.sessionId ?? '-'}</td>
                        <td>{ticketGateDisplay}</td>
                        <td>
                          {checkInLogs ? (
                            <small>
                              <strong>#{checkInLogs.hash}</strong><br />
                              Device: {checkInLogs.sector}<br />
                              {checkInTime}
                            </small>
                          ) : '-'}
                        </td>
                        <td>
                          {renderPurpleAwareActionButton({
                            disabled: disableSign || !isTicketGateCorrect,
                            className: `ticket-action ${isUsed ? 'red' : !isTicketGateCorrect ? wrongGateClass : ''}`,
                            label: !isTicketGateCorrect
                              ? 'Portão Errado'
                              : isUsed
                                ? 'Acesso Já Utilizado'
                                : 'Gerar Acesso',
                            onClick: () => {
                              const tkn = t.hash || t.tokens?.[0]?.token || t.token
                              if (tkn) sessionStorage.setItem('ticket', tkn)
                              if (ticketGate) sessionStorage.setItem('ingPortao', ticketGate)
                              if (t.sectorId) sessionStorage.setItem('sectorId', t.sectorId || '-')
                              handleMotivationFlow()
                            }
                          })}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            )}

            <Table responsive striped >
              <tbody>
                <tr>
                  <td>Foto</td>
                </tr>
                <tr>

                  <td><UserBiometryImage imagePath={userData.image} imageSide={'face'} /></td>
                </tr>

                <tr>
                  <td>Nome</td>
                </tr>
                <tr>
                  <td>{userData.name}</td>
                </tr>

                <tr>
                  <td>
                    {userData.documentType
                      ? `Documento (${userData.documentType})`
                      : 'Documento'}
                  </td>
                </tr>
                <tr>
                  <td>{userData.document}</td>
                </tr>

                <tr>
                  <td>Data de Nascimento</td>
                </tr>
                <tr>
                  <td>{userData.birthday}</td>
                </tr>

                {/* <tr>
                  <td>Transação</td>
                </tr>
                <tr>
                  <td>#{userData.transaction}</td>
                </tr> */}

                <>
                  <tr>
                    <td>Partida:</td>
                  </tr>
                  <tr>
                    <td>{userData.game || '-'}</td>
                  </tr>

                  <tr>
                    <td>Data</td>
                  </tr>
                  <tr>
                    <td>{userData.gameDate || '-'}</td>
                  </tr>

                  <tr>
                    <td>Horário</td>
                  </tr>
                  <tr>
                    <td>{userData.gameHour || '-'}</td>
                  </tr>

                  <tr>
                    <td>Portão de Acesso</td>
                  </tr>
                  <tr>
                    <td>{userData.gate || '-'}</td>
                  </tr>

                  <tr>
                    <td>Seção</td>
                  </tr>
                  <tr>
                    <td>{userData.sectorId || userData.sector || '-'}</td>
                  </tr>

                  <tr>
                    <td>Fileira</td>
                  </tr>
                  <tr>
                    <td>{userData.row || '-'}</td>
                  </tr>

                  <tr>
                    <td>Assento</td>
                  </tr>
                  <tr>
                    <td>{userData.seat || userData.sit || '-'}</td>
                  </tr>
                </>

                {/* <tr>
                  <td>Setor: {userData.sector} Portão: {userData.gate}</td>
                </tr>
                <tr>
                  <td>Bloco: {userData.block} Fileira {userData.row} Assento: {userData.sit}</td>
                </tr> */}

                <>
                  <tr>
                    <td>Número do ingresso</td>
                  </tr>
                  <tr>
                    <td>{userData.ticket}</td>
                  </tr>
                </>
              </tbody>
              <tfoot>
                <tr>
                  <td></td>
                </tr>
              </tfoot>
            </Table>
            {!hasMultipleTickets && isSelectedGateCorrect &&
              renderPurpleAwareActionButton({
                disabled: disableSign,
                type: 'submit',
                className: 'ticket-action',
                label: 'Gerar Acesso',
                onClick: handleDirectAccessFlow
              })
            }

            {userData.ticketStatus === 'invalid' &&
              <Table responsive striped >
                <tbody>
                  <tr className='text-center'>
                    <td>Utilização</td>
                  </tr>
                  <tr>
                    <td className='text-center'>
                      {userData.ticketUsed.date} - {userData.ticketUsed.time} <br />
                      Portão {userData.ticketUsed.gate}
                    </td>
                  </tr>
                </tbody></Table>}
            <Card.Body>

              <Button variant="outline-primary" className='mb-0 w-100 ' onClick={() => setSearchUser('')}>Pesquisar Outro</Button>

            </Card.Body>

          </Card></Col>
      </Row>}

    </>

  )
}

export default SearchTicket
